import { computed, Ref, shallowRef, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import type { ICatalogSection } from '@/domain/catalog/catalog-view';

/** Above this the menu starts with all but the first two categories collapsed. */
export const COLLAPSE_PRODUCTS_FROM = 250;
export const COLLAPSE_CATEGORIES_FROM = 25;
const OPEN_BY_DEFAULT = 2;
const NONE_COLLAPSED: Record<number, true> = Object.freeze({});

/**
 * One static storage entry (VueUse 10 takes a static key) for every store
 * and menu: `{ '{restaurantId}:{menuId ?? 0}': { [categoryId]: true } }`.
 */
const storage = useLocalStorage<Record<string, Record<number, true>>>(
  'menuV2:collapsed',
  {}
);

export interface ICatalogCollapseDeps {
  restaurantId: Ref<number | null>;
  menuId: () => number | null;
  sections: Ref<readonly ICatalogSection[]>;
  productCount: Ref<number>;
  /** Collapse is ignored while a filter or search is active. */
  isFiltering: Ref<boolean>;
}

/** Per-viewer collapsed categories of the Menú page (plan §5.8). */
export function useCatalogCollapse(deps: ICatalogCollapseDeps) {
  const key = computed(
    () => `${deps.restaurantId.value ?? 0}:${deps.menuId() ?? 0}`
  );

  /** Big menus start with all but the first two categories collapsed. */
  function defaultsNow(): Record<number, true> {
    const big =
      deps.productCount.value > COLLAPSE_PRODUCTS_FROM ||
      deps.sections.value.length > COLLAPSE_CATEGORIES_FROM;
    const collapsed: Record<number, true> = {};
    if (big)
      deps.sections.value
        .slice(OPEN_BY_DEFAULT)
        .forEach((section) => (collapsed[section.category.id] = true));
    return collapsed;
  }

  /**
   * The defaults of each store and menu, worked out once (when its sections
   * are first known): a new product or category never collapses a card,
   * which could unmount a focused field (AGENTS keyboard rule).
   *
   * A watcher works them out, not `collapsedIds`: a computed that read the
   * sections would inherit every product's dependencies, and every card's
   * `isCollapsed` call would then walk all of them.
   */
  const frozenDefaults = shallowRef<Record<string, Record<number, true>>>({});
  watch(
    () => (deps.sections.value.length ? key.value : null),
    (readyKey) => {
      if (readyKey === null || readyKey in frozenDefaults.value) return;
      frozenDefaults.value = {
        ...frozenDefaults.value,
        [readyKey]: defaultsNow(),
      };
    },
    { immediate: true }
  );

  const collapsedIds = computed<Record<number, true>>(
    () =>
      storage.value?.[key.value] ??
      frozenDefaults.value[key.value] ??
      NONE_COLLAPSED
  );

  function isCollapsed(categoryId: number): boolean {
    return !deps.isFiltering.value && !!collapsedIds.value[categoryId];
  }

  function setCollapsed(categoryId: number, collapsed: boolean) {
    const next = { ...collapsedIds.value };
    if (collapsed) next[categoryId] = true;
    else delete next[categoryId];
    storage.value = { ...(storage.value ?? {}), [key.value]: next };
  }

  function toggle(categoryId: number) {
    setCollapsed(categoryId, !collapsedIds.value[categoryId]);
  }

  return { collapsedIds, isCollapsed, setCollapsed, toggle };
}

export type ICatalogCollapse = ReturnType<typeof useCatalogCollapse>;
